import { z } from "zod";

/**
 * Interface language, per user.
 *
 * The application ships translated into Czech and English, and each user picks one. Czech is
 * the default because the organisation is Czech — this reverses M4's *interface* half while
 * leaving its documentation half (the book and specs stay English) intact. See
 * `specs/i18n.md`.
 *
 * The catalogue itself lives in `apps/web/src/i18n/` — the API never renders a user-facing
 * sentence, so it needs the *value*, not the vocabulary.
 */
export const LOCALES = ["cs", "en"] as const;

export type Locale = (typeof LOCALES)[number];

export const localeSchema = z.enum(LOCALES);

/** What a new user gets, and what the column defaults to. */
export const DEFAULT_LOCALE: Locale = "cs";

/** Narrow an unknown string to a supported locale, or nothing. */
export function asLocale(value: string | null | undefined): Locale | undefined {
  return (LOCALES as readonly string[]).includes(value ?? "") ? (value as Locale) : undefined;
}

/**
 * The one write. **Self-service and not admin-gated**: a language preference is personal
 * display state, like rows-per-page, so the owner of the session is the only person who may
 * set it and there is nothing for an administrator to manage.
 *
 * It carries no `version`, and changing it writes **no audit event** — auditing "Hana now
 * reads the interface in English" would be noise in a log whose value is that it only holds
 * facts about the pipeline.
 */
export const updateMyLocaleSchema = z.object({ locale: localeSchema });
export type UpdateMyLocaleInput = z.infer<typeof updateMyLocaleSchema>;
